import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { getOr, isEmpty } from 'lodash/fp';
import {
  ButtonToolbar,
  ControlLabel,
  Form,
  FormGroup,
  FormControl,
  Schema,
  Panel,
  SelectPicker,
  Message,
  Toggle,
  HelpBlock,
  Icon,
  Button
} from 'rsuite';

import { GoogleOAuthProvider } from '@react-oauth/google';
import { toastr } from 'react-redux-toastr';
import * as PERMISSIONS from '../../../../constants/permissions/settings';
import { hasModifyAccess } from '../../../../helpers/permission';
import { getUserPermissionsAsMap } from '../../../../redux/selectors/permission';
import { SmtpAccountType } from '../../../../interfaces/smtpAccount';
import { fetchCurrentPantry } from '../../../../redux/Settings/sagas/portal/routines';
import {
  getIsFetched,
  getPantry
} from '../../../../redux/Settings/selectors/portal/pantry';
import {
  SmtpAccountStatus,
  SMTP_TYPES
} from '../../../../constants/smtp-account';
import SmtpAccountConnectionMessage from '../SmtpAccountConnectionMessage';
import SmtpAccountHelperText from '../SmtpAccountHelptext';
import SmtpAccountSubmitButton from '../SmtpAccountSubmitButton';

const { StringType, NumberType } = Schema.Types;

const FormModel = Schema.Model({
  id: NumberType(),
  clientId: StringType().isRequired('This field is required'),
  clientSecret: StringType().isRequired('This field is required')
});

const DefaultValues = {
  id: null,
  clientId: '',
  clientSecret: '',
  clientProfileEmail: '',
  smtpType: SMTP_TYPES.GMAIL,
  status: SmtpAccountStatus.INACTIVE
};

const SmtpAccountForm = ({ data, disabled = false }) => {
  const dispatch = useDispatch();
  const [formValue, setFormValue] = useState(DefaultValues);
  const permissions = useSelector(getUserPermissionsAsMap);
  const allowEdit = hasModifyAccess(PERMISSIONS.SMTP, permissions);
  const isFetched = useSelector(getIsFetched);
  const pantry = useSelector(getPantry);
  const senderName = getOr('...', 'name', pantry);
  const senderEmail = getOr('', 'clientProfileEmail', formValue);
  const clientId = getOr('', 'clientId', formValue);

  const hasClientCredentials = Boolean(
    getOr('', 'clientId', data) && getOr('', 'clientSecret', data)
  );
  const connectionStatusRaw = getOr(0, 'isConnected', data);
  const connectionStatus = Number(connectionStatusRaw);
  const isConnected = connectionStatus === 1;

  const handleGoogleAuthRedirect = () => {
    const winLocation = window.location;
    const params = new URLSearchParams(winLocation.search);
    const status = params.get('status');
    if (status === '1') {
      toastr.success(
        'Success',
        'SMTP Account successfully created and linked with your google account.'
      );
    } else if (status === '0') {
      toastr.error('Error', 'Error authenticating to your google account.');
    }
    window.history.replaceState(null, '', winLocation.pathname);
  };

  const handleFormChange = form => {
    setFormValue(form);
  };

  const handleStatusChange = checked => {
    setFormValue({
      ...formValue,
      status: checked ? SmtpAccountStatus.ACTIVE : SmtpAccountStatus.INACTIVE
    });
  };

  useEffect(
    () => {
      if (!isEmpty(data)) setFormValue(data);
    },
    [data]
  );

  useEffect(() => {
    handleGoogleAuthRedirect();
  }, []);

  const smtpTypes = [
    { label: 'Gmail', value: SMTP_TYPES.GMAIL }
    // { label: 'Outlook', value: SMTP_TYPES.OUTLOOK },
    // { label: 'Sendgrid', value: SMTP_TYPES.SENDGRID },
  ];

  useEffect(
    () => {
      if (!isFetched) dispatch(fetchCurrentPantry());
    },
    [isFetched]
  );

  return (
    <Panel
      header={<SmtpAccountHelperText isConnected={isConnected} />}
      bordered
    >
      <Form
        fluid
        model={FormModel}
        formValue={formValue}
        onChange={handleFormChange}
      >
        <SmtpAccountConnectionMessage
          isConnected={isConnected}
          hasClientCredentials={hasClientCredentials}
          lastAuthError={getOr('', 'lastAuthError', data)}
        />
        <FormGroup>
          <ControlLabel>SMTP Provider</ControlLabel>
          <FormControl
            readOnly
            name="smtpType"
            accepter={SelectPicker}
            placeholder="Select SMTP Provider"
            style={{ width: '100%' }}
            cleanable={false}
            data={smtpTypes}
            disabled={disabled}
          />
        </FormGroup>
        <FormGroup>
          {!allowEdit && (
            <Message
              type="info"
              description="You only have VIEW permission for Smtp Account. Editing form is restricted."
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <ControlLabel>Client ID</ControlLabel>
          <FormControl
            name="clientId"
            readOnly={!allowEdit}
            disabled={disabled}
          />
        </FormGroup>
        <FormGroup>
          <ControlLabel>Client Secret</ControlLabel>
          <FormControl
            name="clientSecret"
            readOnly={!allowEdit}
            disabled={disabled}
          />
        </FormGroup>
        {senderEmail && (
          <FormGroup>
            <ControlLabel>Sender (From)</ControlLabel>
            <FormControl
              readOnly
              value={`${senderName} <${senderEmail}>`}
              disabled={disabled}
            />
            <HelpBlock>
              This is the sender name and email address that will appear in your
              guest&apos;s inbox when they receive your emails. To update this,
              edit the Name field in the Pantry tab.
            </HelpBlock>
          </FormGroup>
        )}
        {formValue.id && (
          <FormGroup>
            <ControlLabel>Status</ControlLabel>
            <FormControl
              name="status"
              readOnly={!allowEdit || !isConnected}
              accepter={Toggle}
              onChange={handleStatusChange}
              checked={Number(formValue.status) === SmtpAccountStatus.ACTIVE}
              checkedChildren="Active"
              unCheckedChildren="Inactive"
              disabled={disabled || !isConnected}
            />
          </FormGroup>
        )}
        <ButtonToolbar style={{ textAlign: 'left' }}>
          {clientId ? (
            <GoogleOAuthProvider clientId={clientId}>
              <SmtpAccountSubmitButton
                formValue={formValue}
                data={data}
                disabled={disabled}
              />
            </GoogleOAuthProvider>
          ) : (
            <Button appearance="primary" disabled>
              <Icon icon="save" /> Submit
            </Button>
          )}
        </ButtonToolbar>
      </Form>
    </Panel>
  );
};

SmtpAccountForm.propTypes = {
  data: SmtpAccountType,
  disabled: PropTypes.bool
};

SmtpAccountForm.defaultProps = {
  data: {},
  disabled: false
};

export default SmtpAccountForm;
